import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@shared/ui/collapsible";
import {
  Plug,
  Droplet,
  Trash2,
  ChevronDown,
  Clock,
  Gauge,
  Plus,
  Minus,
  MinusCircle,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { AddWaterConnectionModal } from "./AddWaterConnectionModal";
import { AddTrashConnectionModal } from "./AddTrashConnectionModal";
import { ReduceTrashQuantityModal } from "./ReduceTrashQuantityModal";
import {
  useConsumerMeterConnectionDetail,
  useAddNewConnection,
  useAddQuantityConnection,
  useMeterReadings,
} from "@features/cx/consumerManagement/hooks";
import {
  PaginatedResponse,
  MeterReading,
} from "@features/cx/consumerManagement/types";
import { TableColumn } from "@shared/types/table";
import { DEFAULT_PAGE_NUMBER, PAGE_SIZE } from "@shared/utils/constants";

interface ConnectionsTabProps {
  consumerNo: string;
  consumerID?: string;
  remoteUtilityId: string;
}

// CamelCase Interfaces for Meter Details & Connection Data
interface MeterDetail {
  id?: number | string;
  meterNumber?: string;
  deviceNo?: string;
  productCode?: string;
  meterTypeDisplay?: string;
  physicalStatusDisplay?: string;
  statusDisplay?: string;
  readingStatusDisplay?: string;
  currentReading?: number | string;
  currentReadingDate?: string;
  lastReading?: number | string;
  lastReadingDate?: string;
  meterModel?: string;
  resourceTypeDisplay?: string;
  resourceSubTypeDisplay?: string;
  routeName?: string;
  cycleName?: string;
}

interface MeterConnectionItemData {
  connectionNumber: number;
  meterId?: number | string;
  meter?: MeterDetail;
}

interface MeterBasedServiceData {
  serviceName: string;
  numberOfConnection: number;
  connectionInfo: MeterConnectionItemData[];
}

interface QuantityBasedServiceData {
  serviceName: string;
  numberOfConnection: number;
  quantity: number;
  action?: string;
  status?: string;
  startDate?: string;
  connectionHistory?: any[];
}

interface QuantityConnection {
  id: string;
  connectionDate: string;
  quantity: number;
}

interface ReductionLogEntry {
  id: string;
  reductionDate: string;
  originalConnectionDate: string;
  originalQuantity: number;
  newQuantity: number;
}

const reductionLogColumns: TableColumn<ReductionLogEntry>[] = [
  {
    key: "reductionDate",
    header: "Reduction Date",
    type: "text",
  },
  {
    key: "originalConnectionDate",
    header: "Original Date",
    type: "text",
  },
  {
    key: "newQuantity",
    header: "Quantity Change",
    type: "custom",
    render: (_, row: ReductionLogEntry) => (
      <div className="flex items-center gap-2 text-xs font-medium">
        <span className="text-gray-500">{row.originalQuantity}</span>
        <ArrowRight className="w-3 h-3 text-gray-400" />
        {row.newQuantity === 0 ? (
          <Badge
            variant="secondary"
            className="bg-red-50 text-red-700 border border-red-200 font-semibold text-xs"
          >
            Removed
          </Badge>
        ) : (
          <span className="font-semibold text-gray-900">{row.newQuantity}</span>
        )}
      </div>
    ),
  },
];

// Helper to select service icon
const getServiceIcon = (serviceName: string) => {
  const name = (serviceName || "").toLowerCase();
  if (name.includes("water"))
    return <Droplet className="w-4 h-4 text-blue-600" />;
  if (name.includes("trash") || name.includes("waste"))
    return <Trash2 className="w-4 h-4 text-gray-700" />;
  if (name.includes("gas")) return <Gauge className="w-4 h-4 text-amber-600" />;
  if (name.includes("electric") || name.includes("power"))
    return <Plug className="w-4 h-4 text-yellow-600" />;
  return <Plug className="w-4 h-4 text-blue-600" />;
};

// CamelCase Data Parser for Meter-Based and Quantity-Based Services
const parseConnectionDetailData = (responseData: any) => {
  const result = responseData?.result || responseData || {};

  const meterBasedRaw = result.meterBased || result.meter_based || {};
  const quantityBasedRaw = result.quantityBased || result.quantity_based || {};

  const meterBasedServices: MeterBasedServiceData[] = Object.entries(
    meterBasedRaw,
  ).map(([serviceName, serviceObj]: [string, any]) => {
    const numberOfConnection =
      serviceObj?.numberOfConnection || serviceObj?.number_of_connection || 0;
    const rawConnInfo =
      serviceObj?.connectionInfo || serviceObj?.connection_info || {};

    const connInfoList: MeterConnectionItemData[] = Object.values(
      rawConnInfo,
    ).map((cItem: any) => {
      const rawMeter = cItem?.meter || {};
      const meter: MeterDetail = {
        id: rawMeter.id || cItem?.meterId || cItem?.meter_id || "N/A",
        meterNumber: rawMeter.meterNumber || rawMeter.meter_number || "N/A",
        deviceNo: rawMeter.deviceNo || rawMeter.device_no || "N/A",
        productCode:
          rawMeter.productCode || rawMeter.product_code || serviceName || "N/A",
        meterTypeDisplay:
          rawMeter.meterTypeDisplay || rawMeter.meter_type_display || "N/A",
        physicalStatusDisplay:
          rawMeter.physicalStatusDisplay ||
          rawMeter.physical_status_display ||
          "Installed",
        statusDisplay:
          rawMeter.statusDisplay || rawMeter.status_display || "Assigned",
        readingStatusDisplay:
          rawMeter.readingStatusDisplay ||
          rawMeter.reading_status_display ||
          "Normal",
        currentReading:
          rawMeter.currentReading ?? rawMeter.current_reading ?? "N/A",
        currentReadingDate:
          rawMeter.currentReadingDate || rawMeter.current_reading_date || "N/A",
        lastReading: rawMeter.lastReading ?? rawMeter.last_reading ?? "N/A",
        lastReadingDate:
          rawMeter.lastReadingDate || rawMeter.last_reading_date || "N/A",
        meterModel: rawMeter.meterModel || rawMeter.meter_model || "N/A",
        resourceTypeDisplay:
          rawMeter.resourceTypeDisplay ||
          rawMeter.resource_type_display ||
          "N/A",
        resourceSubTypeDisplay:
          rawMeter.resourceSubtypeDisplay ||
          rawMeter.resource_subtype_display ||
          rawMeter.resource_sub_type_display ||
          "N/A",
        routeName: rawMeter.routeName || rawMeter.route_name || "N/A",
        cycleName: rawMeter.cycleName || rawMeter.cycle_name || "N/A",
      };

      return {
        connectionNumber:
          cItem?.connectionNumber || cItem?.connection_number || 1,
        meterId: cItem?.meterId || cItem?.meter_id || "N/A",
        meter,
      };
    });

    return {
      serviceName,
      numberOfConnection,
      connectionInfo: connInfoList,
    };
  });

  const quantityBasedServices: QuantityBasedServiceData[] = Object.entries(
    quantityBasedRaw,
  ).map(([serviceName, serviceObj]: [string, any]) => {
    return {
      serviceName,
      numberOfConnection: serviceObj?.numberOfConnection || 0,
      quantity: serviceObj?.quantity || 0,
      action: serviceObj?.action || "N/A",
      status: serviceObj?.status || "N/A",
      startDate: serviceObj?.startDate || serviceObj?.start_date || "N/A",
      connectionHistory:
        serviceObj?.connectionHistory || serviceObj?.connection_history || [],
    };
  });

  return {
    meterBasedServices,
    quantityBasedServices,
  };
};

export const ConnectionsTab: React.FC<ConnectionsTabProps> = ({
  consumerNo,
  consumerID,
  remoteUtilityId,
}) => {
  const { data: meterConnectionData, isLoading } =
    useConsumerMeterConnectionDetail({
      consumer_id: consumerID,
      remote_utility_id: remoteUtilityId,
    });

  const { mutate: addNewConnection, isPending: isSubmitting } =
    useAddNewConnection(consumerID || "");

  // Parse response using camelCase
  const { meterBasedServices, quantityBasedServices } = useMemo(() => {
    return parseConnectionDetailData(meterConnectionData);
  }, [meterConnectionData]);

  // Quantity connections local state for manual additions/reductions
  const [localQuantityConnections, setLocalQuantityConnections] = useState<
    QuantityConnection[]
  >([]);

  // Reductions Log State
  const [reductionLogs, setReductionLogs] = useState<ReductionLogEntry[]>([]);

  // Modals state
  const [isAddMeterModalOpen, setIsAddMeterModalOpen] = useState(false);
  const [activeUtilityName, setActiveUtilityName] = useState("Utility");
  const [isAddTrashModalOpen, setIsAddTrashModalOpen] = useState(false);
  const [activeQServiceName, setActiveQServiceName] = useState("");
  const [activeQServiceQuantity, setActiveQServiceQuantity] = useState(1);
  const [selectedQuantityConn, setSelectedQuantityConn] =
    useState<QuantityConnection | null>(null);
  const [isReduceTrashModalOpen, setIsReduceTrashModalOpen] = useState(false);

  const { mutate: addQuantityConnection, isPending: isQuantitySubmitting } =
    useAddQuantityConnection(consumerID || "", remoteUtilityId || "");

  const handleOpenAddMeterModal = (utility: string = "Utility") => {
    setActiveUtilityName(utility);
    setIsAddMeterModalOpen(true);
  };

  // Called when modal confirms — build payload and POST
  const handleAddMeterConnection = (connectionData: any) => {
    const serviceName = activeUtilityName;
    const existingService = meterBasedServices.find(
      (s) => s.serviceName.toLowerCase() === serviceName.toLowerCase(),
    );
    const existingCount =
      existingService?.numberOfConnection ||
      existingService?.connectionInfo?.length ||
      0;
    const connectionNumber = existingCount + 1;

    const connectionItem = {
      ...(connectionData.connections?.[serviceName]?.[0] || {}),
      connection_number: connectionNumber,
    };

    const payload = {
      connections: {
        [serviceName]: [connectionItem],
      },
    };

    addNewConnection(payload);
    setIsAddMeterModalOpen(false);
  };

  // Handlers for Quantity-Based Connections
  const handleOpenQuantityModal = (serviceName: string, currentQty: number) => {
    setActiveQServiceName(serviceName);
    setActiveQServiceQuantity(currentQty);
    setIsAddTrashModalOpen(true);
  };

  const handleAddQuantityConnection = (newConn: {
    quantity: number;
    startDate?: string;
  }) => {
    addQuantityConnection({
      remote_utility_id: Number(remoteUtilityId),
      utility_service: activeQServiceName,
      quantity: newConn.quantity,
      action: "quantity_based",
      ...(newConn.startDate ? { start_date: newConn.startDate } : {}),
    });
    setIsAddTrashModalOpen(false);
  };

  const handleOpenReduceQuantityModal = (
    conn: QuantityConnection,
    serviceName: string,
  ) => {
    setSelectedQuantityConn(conn);
    setActiveQServiceName(serviceName);
    setIsReduceTrashModalOpen(true);
  };

  const handleReduceQuantity = (
    connId: string,
    newQuantity: number,
    reductionDate?: string,
  ) => {
    addQuantityConnection(
      {
        remote_utility_id: Number(remoteUtilityId),
        utility_service: activeQServiceName,
        quantity: newQuantity,
        action: "quantity_based",
        ...(reductionDate ? { start_date: reductionDate } : {}),
      },
      {
        onSuccess: () => {
          setIsReduceTrashModalOpen(false);

          const targetConn = localQuantityConnections.find(
            (c) => c.id === connId,
          ) || {
            connectionDate: new Date().toISOString(),
            quantity: activeQServiceQuantity,
          };
          if (!targetConn) return;

          // Log reduction
          const newLog: ReductionLogEntry = {
            id: `log-${Date.now()}`,
            reductionDate: reductionDate,
            originalConnectionDate: targetConn.connectionDate,
            originalQuantity: targetConn.quantity,
            newQuantity: newQuantity,
          };
          setReductionLogs((prev) => [newLog, ...prev]);

          if (newQuantity === 0) {
            setLocalQuantityConnections((prev) =>
              prev.filter((c) => c.id !== connId),
            );
          } else {
            setLocalQuantityConnections((prev) =>
              prev.map((c) =>
                c.id === connId ? { ...c, quantity: newQuantity } : c,
              ),
            );
          }
        },
      },
    );
  };

  const nextConnectionNumber = useMemo(() => {
    const activeService = meterBasedServices.find(
      (s) => s.serviceName.toLowerCase() === activeUtilityName.toLowerCase(),
    );
    if (!activeService) return 1;
    const count =
      activeService.numberOfConnection ||
      activeService.connectionInfo?.length ||
      0;
    return count + 1;
  }, [meterBasedServices, activeUtilityName]);

  const hasConnections =
    meterBasedServices.length > 0 || quantityBasedServices.length > 0;

  if (isLoading) {
    return (
      <Card className="bg-white border border-gray-200 shadow-sm rounded-xl p-8 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
        <span className="text-sm font-medium text-gray-600">
          Loading utility connections…
        </span>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-white border border-gray-200 shadow-sm rounded-xl overflow-hidden">
        {/* Main Card Header */}
        <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100">
          <CardTitle className="flex items-center gap-2 text-base font-semibold text-gray-900">
            <Plug className="h-5 w-5 text-blue-600" />
            Utility Connections
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-6 pt-6">
          {!hasConnections ? (
            <div className="text-center py-12 text-gray-500 text-sm italic">
              No utility connections found.
            </div>
          ) : (
            <>
              {/* METER BASED SERVICES SECTION */}
              {meterBasedServices.map((service) => (
                <div
                  key={service.serviceName}
                  className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-xs"
                >
                  {/* Service Title Bar */}
                  <div className="flex items-center justify-between px-5 py-3.5 bg-gray-50/70 border-b border-gray-200">
                    <div className="flex items-center gap-2.5">
                      {getServiceIcon(service.serviceName)}
                      <span className="font-semibold text-sm text-gray-900">
                        {service.serviceName} Service
                      </span>
                      <Badge
                        variant="secondary"
                        className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-medium rounded-full px-2.5 py-0.5"
                      >
                        Meter Based
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge
                        variant="outline"
                        className="bg-white border-gray-200 text-gray-600 text-xs font-semibold px-2.5 py-0.5 rounded-full"
                      >
                        + {service.numberOfConnection}{" "}
                        {service.numberOfConnection === 1
                          ? "connection"
                          : "connections"}
                      </Badge>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() =>
                          handleOpenAddMeterModal(service.serviceName)
                        }
                      >
                        <Plus className="w-3.5 h-3.5" /> Add Connection
                      </Button>
                    </div>
                  </div>

                  {/* Connection Items List */}
                  <div className="p-4 space-y-4">
                    {isSubmitting && (
                      <div className="flex items-center gap-2 text-xs text-blue-600 py-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Adding connection…
                      </div>
                    )}
                    {service.connectionInfo.length === 0 ? (
                      <div className="text-center py-4 text-xs text-gray-400 italic">
                        No active meter connections for {service.serviceName}.
                      </div>
                    ) : (
                      service.connectionInfo.map((connItem, idx) => (
                        <MeterBasedConnectionCard
                          key={connItem.meter?.id || idx}
                          connItem={connItem}
                          index={connItem.connectionNumber || idx + 1}
                          consumerNo={consumerNo}
                          remoteUtilityId={remoteUtilityId}
                        />
                      ))
                    )}
                  </div>
                </div>
              ))}

              {/* QUANTITY BASED SERVICES SECTION */}
              {quantityBasedServices.map((qService) => (
                <div
                  key={qService.serviceName}
                  className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-xs"
                >
                  {/* Quantity Title Bar */}
                  <div className="flex items-center justify-between px-5 py-3.5 bg-gray-50/70 border-b border-gray-200">
                    <div className="flex items-center gap-2.5">
                      {getServiceIcon(qService.serviceName)}
                      <span className="font-bold text-sm text-gray-900">
                        {qService.serviceName} Service
                      </span>
                      <Badge
                        variant="secondary"
                        className="bg-blue-50 text-blue-700 border border-blue-200 text-xs font-semibold rounded-full px-2.5 py-0.5"
                      >
                        Quantity Based
                      </Badge>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handleOpenQuantityModal(
                          qService.serviceName,
                          qService.quantity,
                        )
                      }
                      className="bg-blue-50/50 text-blue-600 border-blue-100 hover:bg-blue-100 hover:text-blue-700"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add Connection
                    </Button>
                  </div>

                  {/* Quantity Service Content */}
                  <div className="p-5 space-y-6">
                    {/* Total Quantity */}
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-sm text-gray-700">
                        Total Quantity
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-sm text-blue-700">
                          {qService.quantity +
                            localQuantityConnections.reduce(
                              (sum, item) => sum + item.quantity,
                              0,
                            )}{" "}
                          <span className="text-gray-400 font-normal">
                            unit
                            {qService.quantity +
                              localQuantityConnections.reduce(
                                (sum, item) => sum + item.quantity,
                                0,
                              ) ===
                            1
                              ? ""
                              : "s"}
                          </span>
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            handleOpenReduceQuantityModal(
                              {
                                id: "all",
                                connectionDate: new Date().toISOString(),
                                quantity: qService.quantity,
                              },
                              qService.serviceName,
                            )
                          }
                          title="Reduce quantity"
                          className="text-gray-400 border border-gray-200 hover:text-red-600 hover:border-red-200 hover:bg-red-50 p-0.5 rounded-full transition-colors"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Connection History */}
                    <div className="space-y-3 pt-2">
                      <div className="flex items-center gap-1.5 text-xs font-bold text-gray-500 border-b border-gray-100 pb-2">
                        <Clock className="w-3.5 h-3.5" />
                        Connection History
                      </div>

                      {/* Table Header */}
                      <div className="grid grid-cols-3 text-[10px] font-bold text-gray-400 pb-2 border-b border-gray-50">
                        <div>Date</div>
                        <div>Event</div>
                        <div className="text-right">Detail</div>
                      </div>

                      {/* API Connection History */}
                      {qService.connectionHistory &&
                      qService.connectionHistory.length > 0
                        ? qService.connectionHistory.map(
                            (historyItem: any, idx: number) => {
                              const isConnected =
                                historyItem.event === "connected" ||
                                historyItem.event?.toLowerCase() ===
                                  "connected";
                              const badgeColor = isConnected
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700";
                              const textColor = isConnected
                                ? "text-green-700"
                                : "text-red-700";
                              return (
                                <div
                                  key={`history-${idx}`}
                                  className="grid grid-cols-3 items-center py-2 text-xs border-b border-gray-50"
                                >
                                  <div className="text-gray-500 font-medium">
                                    {historyItem.date || "N/A"}
                                  </div>
                                  <div>
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${badgeColor} capitalize`}
                                    >
                                      {historyItem.event || "N/A"}
                                    </span>
                                  </div>
                                  <div
                                    className={`text-right font-bold flex items-center justify-end gap-2 ${textColor}`}
                                  >
                                    {historyItem.detail || ""}
                                  </div>
                                </div>
                              );
                            },
                          )
                        : qService.quantity > 0 && (
                            <div className="grid grid-cols-3 items-center py-2 text-xs border-b border-gray-50">
                              <div className="text-gray-500 font-medium">
                                {qService.startDate || "N/A"}
                              </div>
                              <div>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700">
                                  Connected
                                </span>
                              </div>
                              <div className="text-right font-bold text-green-700">
                                +{qService.quantity} unit
                                {qService.quantity === 1 ? "" : "s"}
                              </div>
                            </div>
                          )}

                      {/* Local Quantity Connections */}
                      {localQuantityConnections.map((item) => (
                        <div
                          key={item.id}
                          className="grid grid-cols-3 items-center py-2 text-xs border-b border-gray-50"
                        >
                          <div className="text-gray-500 font-medium">
                            {item.connectionDate}
                          </div>
                          <div>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700">
                              Connected
                            </span>
                          </div>
                          <div className="text-right font-bold text-green-700 flex items-center justify-end gap-2">
                            +{item.quantity} unit
                            {item.quantity === 1 ? "" : "s"}
                            <button
                              type="button"
                              onClick={() =>
                                handleOpenReduceQuantityModal(
                                  item,
                                  qService.serviceName,
                                )
                              }
                              title="Reduce quantity"
                              className="text-gray-400 hover:text-red-600 hover:bg-red-50 p-1 rounded-full transition-colors ml-1"
                            >
                              <MinusCircle className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}

                      {/* Reductions Log */}
                      {reductionLogs.map((log) => (
                        <div
                          key={log.id}
                          className="grid grid-cols-3 items-center py-2 text-xs border-b border-gray-50"
                        >
                          <div className="text-gray-500 font-medium">
                            {log.reductionDate}
                          </div>
                          <div>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">
                              Reduced
                            </span>
                          </div>
                          <div className="text-right font-bold text-red-700">
                            {log.originalQuantity - log.newQuantity} unit
                            {log.originalQuantity - log.newQuantity === 1
                              ? ""
                              : "s"}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <AddWaterConnectionModal
        isOpen={isAddMeterModalOpen}
        onClose={() => setIsAddMeterModalOpen(false)}
        onAddConnection={handleAddMeterConnection}
        utilityName={activeUtilityName}
        consumerId={consumerID}
        remoteUtilityId={remoteUtilityId}
        connectionNumber={nextConnectionNumber}
      />

      <AddTrashConnectionModal
        isOpen={isAddTrashModalOpen}
        onClose={() => setIsAddTrashModalOpen(false)}
        utilityName={activeQServiceName}
        defaultQuantity={activeQServiceQuantity}
        onAddConnection={handleAddQuantityConnection}
      />

      <ReduceTrashQuantityModal
        isOpen={isReduceTrashModalOpen}
        onClose={() => {
          setIsReduceTrashModalOpen(false);
          setSelectedQuantityConn(null);
        }}
        connection={selectedQuantityConn}
        onReduce={handleReduceQuantity}
      />
    </div>
  );
};

// Sub-component for individual Meter Based Connection Card
interface MeterBasedConnectionCardProps {
  connItem: any;
  index: number;
  consumerNo: string;
  remoteUtilityId: string;
}

const MeterBasedConnectionCard: React.FC<MeterBasedConnectionCardProps> = ({
  connItem,
  index,
  consumerNo,
  remoteUtilityId,
}) => {
  const [isConnectionOpen, setIsConnectionOpen] = useState(true);
  const [isMeterDetailsOpen, setIsMeterDetailsOpen] = useState(true);
  const [isReadingsOpen, setIsReadingsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE_NUMBER);
  const [hasBeenClicked, setHasBeenClicked] = useState(false);

  const meter = connItem.meter || {};
  console.log("Meter Details:", meter);
  const { data: readings, isLoading: loadingReadings } = useMeterReadings(
    consumerNo,
    String(meter.id || ""),
    { page: currentPage, limit: PAGE_SIZE },
    Number(remoteUtilityId),
    hasBeenClicked,
  );

  const handleReadingsToggle = (open: boolean) => {
    setIsReadingsOpen(open);
    if (open && !hasBeenClicked) {
      setCurrentPage(DEFAULT_PAGE_NUMBER);
      setHasBeenClicked(true);
    }
  };

  const readingResults: MeterReading[] =
    (readings as PaginatedResponse<MeterReading>)?.results || [];
  const totalReadings =
    (readings as PaginatedResponse<MeterReading>)?.count || 0;

  return (
    <Collapsible
      open={isConnectionOpen}
      onOpenChange={setIsConnectionOpen}
      className="border border-gray-200 rounded-xl overflow-hidden bg-white"
    >
      {/* Numbered Header Band */}
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50/80 cursor-pointer hover:bg-gray-100/80 transition-colors border-b border-gray-200">
          <span className="text-xs font-bold text-gray-600 tracking-wider">
            Connection {index}
          </span>
          <ChevronDown
            className={`w-4 h-4 text-gray-400 transition-transform ${isConnectionOpen ? "rotate-180" : ""}`}
          />
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent className="p-4 space-y-3">
        {/* Meter Details Sub-Panel */}
        <Collapsible
          open={isMeterDetailsOpen}
          onOpenChange={setIsMeterDetailsOpen}
          className="border border-gray-200 rounded-xl overflow-hidden"
        >
          <CollapsibleTrigger asChild>
            <div className="flex items-center justify-between px-4 py-2.5 bg-white cursor-pointer hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-2">
                <Gauge className="w-4 h-4 text-gray-500" />
                <span className="text-xs font-semibold text-gray-800">
                  Resource Details
                </span>
              </div>
              <ChevronDown
                className={`w-4 h-4 text-gray-400 transition-transform ${isMeterDetailsOpen ? "rotate-180" : ""}`}
              />
            </div>
          </CollapsibleTrigger>

          <CollapsibleContent className="p-4 border-t border-gray-100 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4">
              <ValueLabel
                label="Resource ID"
                value={meter.meterNumber || "-"}
              />
              <ValueLabel label="Serial No" value={meter.deviceNo || "-"} />
              <ValueLabel
                label="Resource Type"
                value={meter.resourceTypeDisplay || "N/A"}
              />
              <ValueLabel
                label="Resource Sub Type"
                value={meter.resourceSubTypeDisplay || "N/A"}
              />
              <ValueLabel label="Model" value={meter.meterModel || "N/A"} />
              <ValueLabel
                label="Assignment Date"
                value={meter.currentReadingDate || meter.lastReadingDate || "-"}
              />
              <ValueLabel
                label="Installation Date"
                value={meter.currentReadingDate || meter.lastReadingDate || "-"}
              />
              <ValueLabel
                label="Reading Status"
                value={
                  <span className="flex items-center text-xs font-semibold text-gray-900">
                    <span className="h-2 w-2 mr-1.5 rounded-full bg-green-500"></span>
                    {meter.readingStatusDisplay || "Normal"}
                  </span>
                }
              />
              <ValueLabel
                label="Last Reading"
                value={
                  meter.lastReading !== undefined
                    ? `${meter.lastReading} (${meter.lastReadingDate || "-"})`
                    : "-"
                }
              />
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Monthly Meter Readings Sub-Panel */}
        <Collapsible
          open={isReadingsOpen}
          onOpenChange={handleReadingsToggle}
          className="border border-gray-200 rounded-xl overflow-hidden"
        >
          <CollapsibleTrigger asChild>
            <div className="flex items-center justify-between px-4 py-2.5 bg-white cursor-pointer hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500" />
                <span className="text-xs font-semibold text-gray-800">
                  Monthly Meter Readings
                </span>
                {!hasBeenClicked && (
                  <span className="ml-1 text-[10px] text-gray-400 italic">
                    — click to load
                  </span>
                )}
              </div>
              <ChevronDown
                className={`w-4 h-4 text-gray-400 transition-transform ${isReadingsOpen ? "rotate-180" : ""}`}
              />
            </div>
          </CollapsibleTrigger>

          <CollapsibleContent className="border-t border-gray-100 bg-white overflow-x-auto">
            {!hasBeenClicked ? (
              <div className="p-6 text-center text-xs text-gray-400 italic">
                Click above to load readings.
              </div>
            ) : loadingReadings ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-blue-500 mr-2" />
                <span className="text-xs text-gray-500">Loading readings…</span>
              </div>
            ) : readingResults.length > 0 ? (
              <GenericTable
                data={readingResults}
                columns={[
                  { key: "updatedOn", header: "Date", type: "text" },
                  { key: "meterReading", header: "Reading", type: "text" },
                  {
                    key: "status",
                    header: "Conditional Status",
                    type: "custom",
                    render: (val: string) => (
                      <Badge className="bg-emerald-100 text-emerald-800 border-0 font-semibold text-xs rounded-full px-2.5 py-0.5">
                        {val || "N/A"}
                      </Badge>
                    ),
                  },
                  {
                    key: "ruleStatus",
                    header: "VE Status",
                    type: "custom",
                    render: (val: string) => (
                      <Badge className="bg-emerald-100 text-emerald-800 border-0 font-semibold text-xs rounded-full px-2.5 py-0.5">
                        {val || "N/A"}
                      </Badge>
                    ),
                  },
                  {
                    key: "validatedBy",
                    header: "Validated by",
                    type: "text",
                  },
                  {
                    key: "validatedOn",
                    header: "Validated on",
                    type: "text",
                  },
                ]}
                rowKey="id"
                pagination={{
                  currentPage,
                  totalPages: Math.ceil(totalReadings / PAGE_SIZE),
                  totalItems: totalReadings,
                  itemsPerPage: PAGE_SIZE,
                  startItem: (currentPage - 1) * PAGE_SIZE + 1,
                  endItem: Math.min(currentPage * PAGE_SIZE, totalReadings),
                }}
                onPageChange={(page) => setCurrentPage(page)}
              />
            ) : (
              <div className="p-4 text-xs text-gray-400 italic text-center">
                No meter readings recorded.
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default ConnectionsTab;
